import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createTask, updateTask, TaskStatus } from '../../store/slices/taskSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import TaskProgressControl, { normalizeTaskProgress } from './TaskProgressControl';
import CompactRichEditor from '../common/CompactRichEditor';
import PostCompletionEditor, { cloneFollowUps } from '../templates/PostCompletionEditor';
import SpacePicker from './SpacePicker';
import {
  ESTIMATE_CUSTOM_UNITS,
  ESTIMATE_CUSTOM_VALUE,
  ESTIMATE_MINUTES_OPTIONS,
  customEstimateToMinutes,
  formatEstimateLabel,
  isPresetEstimateMinutes,
  minutesToCustomEstimate,
} from '../../utils/estimateTime';
import {
  VOICE_TASK_EVENT,
  addNeedToCallFromSpoken,
  matchPersonBySpokenName,
} from '../../utils/voiceTaskCommands';

const STATUS_OPTIONS = [
  { value: TaskStatus.PENDING, label: 'Pending', hint: 'Not started yet' },
  { value: TaskStatus.PICK_TODAY, label: 'Pick today', hint: 'Aim to finish today' },
  { value: TaskStatus.IN_PROGRESS, label: 'In progress', hint: 'Actively working' },
  { value: TaskStatus.COMPLETED, label: 'Completed', hint: 'Done' },
  { value: TaskStatus.CANCELLED, label: 'Cancelled', hint: 'Won’t do' },
];

const TAG_MAX_LEN = 64;
const TAG_MAX_COUNT = 20;

const personDisplayName = (person) => (
  [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim()
  || person?.username
  || person?.email
  || 'Unknown user'
);

const escapeHtml = (text) =>
  String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const spokenToDescriptionHtml = (spoken) => {
  const text = String(spoken || '').trim();
  if (!text) return '';
  return `<p>${escapeHtml(text)}</p>`;
};

const TaskCreationDialog = ({
  isOpen,
  onClose,
  initialTitle = '',
  initialDescription = '',
  messageId: messageIdProp = '',
  mapTaskId: mapTaskIdProp = null,
  editingTask = null,
  onSaved,
  initialDelegateId = '',
  defaultStatus = TaskStatus.PENDING,
  defaultSpaceId = '',
  defaultSpaceName = '',
  variant = 'modal',
  descriptionHint: descriptionHintProp,
  fromVoice = false,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { users, loading: usersLoading } = useSelector(
    (state) => state.users
  );

  const [title, setTitle] = useState('');
  const [estimateTime, setEstimateTime] = useState('15');
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [customEstimateValue, setCustomEstimateValue] = useState('');
  const [customEstimateUnit, setCustomEstimateUnit] = useState('minutes');
  const [description, setDescription] = useState('');
  const [delegateId, setDelegateId] = useState([]);
  const [status, setStatus] = useState(defaultStatus);
  const [tags, setTags] = useState([]);
  const [postCompletionEnabled, setPostCompletionEnabled] = useState(false);
  const [postCompletionActions, setPostCompletionActions] = useState([]);
  const [tagDraft, setTagDraft] = useState('');
  const [delegateQuery, setDelegateQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [savedTask, setSavedTask] = useState(null);
  const [spaceId, setSpaceId] = useState('');
  const [spaceName, setSpaceName] = useState('');
  const quillRef = useRef(null);
  const tagInputRef = useRef(null);
  const formInitedRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const pendingDelegateNameRef = useRef('');

  const activeTask = savedTask || editingTask;
  const isEdit = Boolean(activeTask?.id);
  const mapTaskIdForCreate = mapTaskIdProp || '';

  const hasMeaningfulContent = useCallback((htmlContent) => {
    if (!htmlContent || htmlContent === '<p><br></p>' || htmlContent === '<p></p>') {
      return false;
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = htmlContent;
    const textContent = tmp.textContent || tmp.innerText || '';
    if (textContent.trim().length > 0) return true;
    return Boolean(tmp.querySelector('img, video, audio, iframe'));
  }, []);

  const people = useMemo(() => {
    const list = [...(users || [])];
    if (user?.id && !list.some((person) => person.id === user.id)) {
      list.unshift(user);
    }
    return list
      .filter((person) => person && (person.id || person._id))
      .sort((a, b) => {
        const aSelf = a.id === user?.id ? 0 : 1;
        const bSelf = b.id === user?.id ? 0 : 1;
        if (aSelf !== bSelf) return aSelf - bSelf;
        return personDisplayName(a).localeCompare(personDisplayName(b));
      });
  }, [users, user]);

  const filteredPeople = useMemo(() => {
    const q = delegateQuery.trim().toLowerCase();
    if (!q) return people;
    return people.filter((person) => {
      const hay = `${personDisplayName(person)} ${person.username || ''} ${person.email || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [people, delegateQuery]);

  const selectedDelegates = useMemo(
    () => people.filter((person) => delegateId.some((id) => String(id) === String(person.id))),
    [people, delegateId],
  );

  useEffect(() => {
    if (isOpen) {
      setSavedTask(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchUsers({ force: true }));
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      formInitedRef.current = false;
      return;
    }
    if (formInitedRef.current && !editingTask) return;
    formInitedRef.current = true;

    if (editingTask) {
      setTitle(editingTask.title || '');
      const mins = editingTask.estimateTimeInMinutes;
      if (mins != null && mins !== '' && Number(mins) > 0) {
        if (isPresetEstimateMinutes(mins)) {
          setEstimateTime(String(mins));
          setCustomEstimateValue('');
          setCustomEstimateUnit('minutes');
        } else {
          const custom = minutesToCustomEstimate(mins);
          setEstimateTime(ESTIMATE_CUSTOM_VALUE);
          setCustomEstimateValue(custom.value);
          setCustomEstimateUnit(custom.unit);
        }
      } else {
        setEstimateTime('');
        setCustomEstimateValue('');
        setCustomEstimateUnit('minutes');
      }
      const desc = editingTask.description || '';
      setDescription(desc);

      const initialDelegates = Array.isArray(editingTask.delegateId)
        ? editingTask.delegateId
        : editingTask.delegateId ? [editingTask.delegateId] : [];
      setDelegateId(initialDelegates);

      setStatus(editingTask.status || TaskStatus.PENDING);
      setCompletionPercentage(
        normalizeTaskProgress(editingTask.completionPercentage, editingTask.status)
      );
      setTags(Array.isArray(editingTask.tags) ? editingTask.tags.filter(Boolean) : []);
      setPostCompletionEnabled(Boolean(editingTask.postCompletionEnabled));
      setPostCompletionActions(cloneFollowUps(editingTask.postCompletionActions));
      setTagDraft('');
      setDelegateQuery('');
      setSpaceId(editingTask.spaceId || '');
      setSpaceName(editingTask.spaceName || '');
      setHasContent(hasMeaningfulContent(desc));
      const t = setTimeout(() => {
        const quillEditor = quillRef.current?.getEditor();
        if (quillEditor) {
          quillEditor.root.innerHTML = desc || '';
          setHasContent(hasMeaningfulContent(quillEditor.root.innerHTML));
        }
      }, 100);
      return () => clearTimeout(t);
    }

    setTitle(initialTitle || '');
    setEstimateTime('15');
    setCustomEstimateValue('');
    setCustomEstimateUnit('minutes');

    const initialDelegates = Array.isArray(initialDelegateId)
      ? initialDelegateId
      : initialDelegateId
        ? [initialDelegateId]
        : (user?.id && !mapTaskIdForCreate && !fromVoice ? [user.id] : []);
    setDelegateId(initialDelegates);

    setStatus(defaultStatus || TaskStatus.PENDING);
    setCompletionPercentage(0);
    setTags([]);
    setPostCompletionEnabled(false);
    setPostCompletionActions([]);
    setTagDraft('');
    setDelegateQuery('');
    setDescription(initialDescription);
    setHasContent(hasMeaningfulContent(initialDescription));
    setSpaceId(defaultSpaceId || '');
    setSpaceName(defaultSpaceName || '');
    const t = setTimeout(() => {
      const quillEditor = quillRef.current?.getEditor();
      if (quillEditor) {
        if (initialDescription) {
          quillEditor.root.innerHTML = initialDescription;
          setHasContent(hasMeaningfulContent(initialDescription));
        } else {
          quillEditor.setText('');
          setHasContent(false);
        }
      }
    }, 100);
    return () => clearTimeout(t);
  }, [isOpen, editingTask, initialTitle, initialDescription, hasMeaningfulContent, initialDelegateId, defaultStatus, defaultSpaceId, defaultSpaceName, fromVoice, mapTaskIdForCreate, user?.id]);

  useEffect(() => {
    if (!isOpen) {
      setSaving(false);
    }
  }, [isOpen]);

  const toggleDelegate = (userId) => {
    setDelegateId((prev) => (
      prev.some((id) => String(id) === String(userId))
        ? prev.filter((id) => String(id) !== String(userId))
        : [...prev, userId]
    ));
  };

  const resetEstimateFields = () => {
    setEstimateTime('15');
    setCustomEstimateValue('');
    setCustomEstimateUnit('minutes');
  };

  const handleEstimateSelectChange = (value) => {
    setEstimateTime(value);
    if (value !== ESTIMATE_CUSTOM_VALUE) {
      setCustomEstimateValue('');
      setCustomEstimateUnit('minutes');
    }
  };

  const resolveEstimateMinutes = () => {
    if (!estimateTime) return { value: null };
    if (estimateTime === ESTIMATE_CUSTOM_VALUE) {
      const minutes = customEstimateToMinutes(customEstimateValue, customEstimateUnit);
      if (minutes == null) {
        return { error: 'Enter a custom estimate greater than 0' };
      }
      return { value: minutes };
    }
    const n = parseInt(estimateTime, 10);
    return Number.isNaN(n) ? { value: null } : { value: n };
  };

  const addTag = (raw) => {
    const next = String(raw || '').trim().replace(/\s+/g, ' ').slice(0, TAG_MAX_LEN);
    if (!next) return;
    setTags((prev) => {
      if (prev.some((tag) => tag.toLowerCase() === next.toLowerCase())) return prev;
      if (prev.length >= TAG_MAX_COUNT) {
        dispatch(addNotification({
          type: 'error',
          message: `You can add up to ${TAG_MAX_COUNT} tags`,
        }));
        return prev;
      }
      return [...prev, next];
    });
    setTagDraft('');
  };

  const removeTag = (label) => {
    setTags((prev) => prev.filter((tag) => tag !== label));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagDraft);
    } else if (e.key === 'Backspace' && !tagDraft && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const spaceLocked = Boolean(
    (Array.isArray(activeTask?.workflowMessageIds) && activeTask.workflowMessageIds.some(Boolean))
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const quillEditor = quillRef.current?.getEditor();
    const htmlContent = quillEditor ? quillEditor.root.innerHTML : description;

    if (!title.trim() && !hasMeaningfulContent(htmlContent)) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Either Title or Description is required',
        })
      );
      return;
    }

    const estimateResult = resolveEstimateMinutes();
    if (estimateResult.error) {
      dispatch(
        addNotification({
          type: 'error',
          message: estimateResult.error,
        })
      );
      return;
    }

    try {
      setSaving(true);

      const estimateTimeInMinutes = estimateResult.value;
      const normalizedTags = tags
        .map((tag) => String(tag || '').trim())
        .filter(Boolean)
        .slice(0, TAG_MAX_COUNT);

      if (isEdit) {
        const taskData = {
          title: title.trim() || '',
          estimateTimeInMinutes,
          description: htmlContent,
          messageId: activeTask.messageId || messageIdProp || '',
          mapTaskId: activeTask.mapTaskId || mapTaskIdForCreate || '',
          delegateId,
          status,
          completionPercentage,
          tags: normalizedTags,
          postCompletionEnabled,
          postCompletionActions,
          spaceId,
          spaceName,
        };
        const updated = await dispatch(
          updateTask({
            taskId: activeTask.id,
            taskData,
          })
        ).unwrap();
        if (updated?.id) setSavedTask(updated);
        dispatch(
          addNotification({
            type: 'success',
            message: 'Task saved',
          })
        );
      } else {
        const assignedIds = (Array.isArray(delegateId) ? delegateId : [])
          .filter((id) => id && String(id) !== String(user?.id));
        const taskData = {
          title: title.trim() || '',
          estimateTimeInMinutes,
          description: htmlContent,
          messageId: messageIdProp || '',
          mapTaskId: mapTaskIdForCreate || '',
          delegateId: assignedIds,
          status,
          completionPercentage,
          tags: normalizedTags,
          postCompletionEnabled,
          postCompletionActions,
          spaceId,
          spaceName,
        };
        const created = await dispatch(createTask(taskData)).unwrap();
        if (created?.id) setSavedTask(created);
        dispatch(
          addNotification({
            type: 'success',
            message: 'Task saved',
          })
        );
      }

      if (typeof onSaved === 'function') {
        onSaved();
      }
    } catch (error) {
      console.error('Failed to save task:', error);
      dispatch(
        addNotification({
          type: 'error',
          message: error || (isEdit ? 'Failed to update task' : 'Failed to create task. Please try again.'),
        })
      );
    } finally {
      setSaving(false);
    }
  };

  handleSubmitRef.current = handleSubmit;

  const applyVoiceDescription = useCallback((spoken, append = false) => {
    const html = spokenToDescriptionHtml(spoken);
    if (!html) return;
    const quillEditor = quillRef.current?.getEditor();
    const current = quillEditor ? quillEditor.root.innerHTML : description;
    const empty = !current || current === '<p><br></p>' || current === '<p></p>';
    const next = append && !empty ? `${current}${html}` : html;
    setDescription(next);
    setHasContent(true);
    if (quillEditor) {
      quillEditor.root.innerHTML = next;
    }
  }, [description]);

  const assignVoiceDelegate = useCallback((personId) => {
    const id = String(personId || '');
    if (!id) return;
    const selfId = String(user?.id || '');
    setDelegateId((prev) => {
      const withoutSelf = prev.filter((existing) => String(existing) !== selfId);
      if (withoutSelf.some((existing) => String(existing) === id)) return withoutSelf;
      return [...withoutSelf, id];
    });
    setDelegateQuery('');
  }, [user?.id]);

  const applyVoiceDelegate = useCallback((spoken) => {
    const name = String(spoken || '').trim();
    if (!name) return;
    const others = people.filter((person) => String(person.id) !== String(user?.id));
    const matched = matchPersonBySpokenName(name, others);
    if (matched?.id) {
      pendingDelegateNameRef.current = '';
      assignVoiceDelegate(matched.id);
      return;
    }
    pendingDelegateNameRef.current = name;
    setDelegateQuery(name);
    if (people.length) {
      dispatch(addNotification({
        type: 'info',
        message: `Could not match ${name} yet — check people on the task`,
      }));
    }
  }, [assignVoiceDelegate, dispatch, people, user?.id]);

  useEffect(() => {
    const pending = pendingDelegateNameRef.current;
    if (!isOpen || !pending || !people.length) return;
    const others = people.filter((person) => String(person.id) !== String(user?.id));
    const matched = matchPersonBySpokenName(pending, others);
    if (!matched?.id) return;
    pendingDelegateNameRef.current = '';
    assignVoiceDelegate(matched.id);
  }, [assignVoiceDelegate, isOpen, people, user?.id]);

  useEffect(() => {
    const onVoice = (event) => {
      const action = event.detail?.action;
      const value = String(event.detail?.value || '').trim();
      const append = Boolean(event.detail?.append);
      if (!action || action === 'open') return;
      if (!isOpen) return;
      if (action === 'title' && value) setTitle(value);
      if (action === 'description' && value) applyVoiceDescription(value, append);
      if (action === 'delegate' && value) applyVoiceDelegate(value);
      if (action === 'publish') {
        handleSubmitRef.current?.({ preventDefault() {} });
      }
    };
    window.addEventListener(VOICE_TASK_EVENT, onVoice);
    return () => window.removeEventListener(VOICE_TASK_EVENT, onVoice);
  }, [applyVoiceDelegate, applyVoiceDescription, isOpen]);

  const handleCancel = () => {
    setTitle('');
    resetEstimateFields();
    setDelegateId([]);
    setTags([]);
    setTagDraft('');
    setDelegateQuery('');
    setDescription('');
    onClose();
  };

  if (!isOpen) return null;

  const headerTitle = (() => {
    if (isEdit) return 'Task';
    if (mapTaskIdForCreate) return 'Create Mapped Task';
    return 'Create Task';
  })();

  const descriptionHint = descriptionHintProp
    || (fromVoice
      ? 'Speak: add description …  ·  or keep description as …'
      : (hasMeaningfulContent(initialDescription)
        ? 'Pre-filled from the chat message — edit as needed.'
        : 'Describe what needs to be done.'));
  const titlePlaceholder = fromVoice
    ? 'Speak: add title …  (or keep title as …)'
    : 'What needs to be done?';
  const descriptionPlaceholder = fromVoice
    ? 'Speak: add description …  (or keep description as …)'
    : 'Add context, links, or steps…';
  const delegatePlaceholder = fromVoice
    ? 'Speak: delegate to [name]'
    : 'Search people…';
  const isSidebar = variant === 'sidebar';

  const fieldClass =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';
  const panelClass =
    'rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 p-4 space-y-4';

  const dialog = (
        <div
          className={isSidebar
            ? 'h-full min-h-0 flex flex-col bg-white dark:bg-gray-900'
            : 'relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-5xl w-full transform transition-all'}
          role="dialog"
          aria-labelledby="task-create-dialog-title"
        >
          <div className={`flex items-center justify-between border-b border-gray-200 dark:border-gray-700 ${isSidebar ? 'px-4 py-3' : 'px-6 py-4'}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 text-primary-600 dark:text-primary-400">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <h3
                  id="task-create-dialog-title"
                  className="text-lg font-semibold text-gray-900 dark:text-white truncate"
                >
                  {headerTitle}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isSidebar ? 'Fill in the details, then save.' : 'Details on the left · people & status on the right'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className={isSidebar ? 'flex-1 min-h-0 flex flex-col' : undefined}>
            <div className={isSidebar ? 'flex-1 min-h-0 overflow-y-auto p-4' : 'p-5 sm:p-6'}>
              <div className={isSidebar ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 lg:grid-cols-2 gap-5'}>
                {/* Left: title, description, duration */}
                <section className={panelClass} aria-label="Task details">
                  <div>
                    <label htmlFor="task-create-title" className={labelClass}>
                      Title
                    </label>
                    <input
                      id="task-create-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className={fieldClass}
                      placeholder={titlePlaceholder}
                      autoFocus
                    />
                  </div>

                  <SpacePicker
                    valueId={spaceId}
                    valueName={spaceName}
                    locked={spaceLocked}
                    lockedHint="Tasks in a workflow stay in that workflow’s space."
                    onChange={({ spaceId: nextId, spaceName: nextName }) => {
                      setSpaceId(nextId || '');
                      setSpaceName(nextName || '');
                    }}
                  />

                  <div>
                    <label className={labelClass}>Description</label>
                    <CompactRichEditor
                      quillRef={quillRef}
                      value={description}
                      onChange={(content) => {
                        setDescription(content);
                        setHasContent(hasMeaningfulContent(content));
                      }}
                      placeholder={descriptionPlaceholder}
                      minHeight={isSidebar ? 120 : 160}
                      maxHeight={isSidebar ? 220 : 260}
                      instanceClass="task-create-quill"
                    />
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{descriptionHint}</p>
                  </div>

                  <div>
                    <label htmlFor="task-create-duration" className={labelClass}>
                      Duration
                    </label>
                    <select
                      id="task-create-duration"
                      value={estimateTime}
                      onChange={(e) => handleEstimateSelectChange(e.target.value)}
                      className={fieldClass}
                    >
                      <option value="">None (optional)</option>
                      {ESTIMATE_MINUTES_OPTIONS.map((m) => (
                        <option key={m} value={String(m)}>
                          {formatEstimateLabel(m)}
                        </option>
                      ))}
                      <option value={ESTIMATE_CUSTOM_VALUE}>Custom</option>
                    </select>
                    {estimateTime === ESTIMATE_CUSTOM_VALUE && (
                      <div className="mt-2 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Value
                          </label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={customEstimateValue}
                            onChange={(e) => setCustomEstimateValue(e.target.value)}
                            className={fieldClass}
                            placeholder="e.g. 2"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Unit
                          </label>
                          <select
                            value={customEstimateUnit}
                            onChange={(e) => setCustomEstimateUnit(e.target.value)}
                            className={fieldClass}
                          >
                            {ESTIMATE_CUSTOM_UNITS.map((unit) => (
                              <option key={unit.value} value={unit.value}>
                                {unit.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Right: delegate, status, tags */}
                <section className={panelClass} aria-label="Assignment and status">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Assign to
                      </label>
                      {selectedDelegates.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setDelegateId([])}
                          className="text-[11px] font-medium text-gray-500 hover:text-rose-600 dark:hover:text-rose-400"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    {selectedDelegates.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {selectedDelegates.map((person) => {
                          const isSelf = person.id === user?.id;
                          const name = personDisplayName(person);
                          return (
                            <button
                              key={person.id}
                              type="button"
                              onClick={() => toggleDelegate(person.id)}
                              className="inline-flex items-center gap-1 max-w-full rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 border border-primary-200 dark:border-primary-700 px-2.5 py-1 text-xs font-medium hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                              title={`Remove ${name}`}
                            >
                              <span className="truncate">{isSelf ? `You (${name})` : name}</span>
                              <span className="opacity-70" aria-hidden="true">×</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <input
                      type="search"
                      value={delegateQuery}
                      onChange={(e) => setDelegateQuery(e.target.value)}
                      placeholder={delegatePlaceholder}
                      className={`${fieldClass} mb-2 text-sm`}
                      autoComplete="off"
                    />

                    <div className={`overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700 ${fromVoice ? 'max-h-72' : 'max-h-56'}`}>
                      {usersLoading ? (
                        <div className="px-3 py-3 text-sm text-gray-500">Loading people…</div>
                      ) : filteredPeople.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-500">No matches</div>
                      ) : (
                        filteredPeople.map((person) => {
                          const isSelf = person.id === user?.id;
                          const name = personDisplayName(person);
                          const checked = delegateId.some((id) => String(id) === String(person.id));
                          return (
                            <label
                              key={person.id}
                              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
                                checked
                                  ? 'bg-primary-50/80 dark:bg-primary-900/20'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'
                              }`}
                            >
                              <input
                                type="checkbox"
                                data-voice-delegate-id={person.id}
                                checked={checked}
                                onChange={() => toggleDelegate(person.id)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                {isSelf ? `You (${name})` : name}
                                {!isSelf && person.username && person.firstName ? (
                                  <span className="text-gray-400 dark:text-gray-500"> ({person.username})</span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      Optional — leave empty to keep it with you.
                    </p>
                  </div>

                  <div>
                    <label className={labelClass}>Status</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {STATUS_OPTIONS.map((option) => {
                        const active = status === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            title={option.hint}
                            onClick={() => {
                              setStatus(option.value);
                              if (option.value === TaskStatus.COMPLETED) {
                                setCompletionPercentage(100);
                              } else if (status === TaskStatus.COMPLETED) {
                                setCompletionPercentage(0);
                              }
                            }}
                            className={`rounded-lg border px-2.5 py-2 text-left transition-all ${
                              active
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500/30 text-primary-900 dark:text-primary-100'
                                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <span className="block text-xs font-semibold leading-tight">{option.label}</span>
                            <span className="block text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">
                              {option.hint}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {status !== TaskStatus.CANCELLED && (
                    <div>
                      <label className={labelClass}>Progress</label>
                      <TaskProgressControl
                        value={completionPercentage}
                        collapsible={false}
                        onChange={(pct) => {
                          setCompletionPercentage(pct);
                          if (pct >= 100) {
                            setStatus(TaskStatus.COMPLETED);
                          } else if (status === TaskStatus.COMPLETED) {
                            setStatus(TaskStatus.IN_PROGRESS);
                          }
                        }}
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="task-create-tag" className={labelClass}>
                      Tags
                    </label>
                    <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 min-h-[44px]">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 px-2 py-0.5 text-xs font-medium"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="opacity-60 hover:opacity-100 leading-none"
                              aria-label={`Remove tag ${tag}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <input
                          ref={tagInputRef}
                          id="task-create-tag"
                          type="text"
                          value={tagDraft}
                          maxLength={TAG_MAX_LEN}
                          onChange={(e) => setTagDraft(e.target.value)}
                          onKeyDown={handleTagKeyDown}
                          onBlur={() => {
                            if (tagDraft.trim()) addTag(tagDraft);
                          }}
                          placeholder={tags.length ? 'Add another…' : 'Type a tag and press Enter'}
                          className="flex-1 min-w-[8rem] bg-transparent border-0 outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 py-1"
                        />
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      Press Enter or comma to add. Up to {TAG_MAX_COUNT} tags.
                    </p>
                  </div>

                  <PostCompletionEditor
                    enabled={postCompletionEnabled}
                    actions={postCompletionActions}
                    users={users || []}
                    title="Post completion"
                    description="When this task is marked complete, also post a message, announcement, or the other follow-ups below."
                    onChange={({ enabled, actions }) => {
                      setPostCompletionEnabled(enabled);
                      setPostCompletionActions(actions);
                    }}
                  />
                </section>
              </div>
            </div>

            <div className={`border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/20 ${isSidebar ? 'px-4 py-3' : 'px-6 py-4 rounded-b-xl'}`}>
              <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={saving || !(title.trim() || hasContent)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  'Save'
                )}
              </button>
              </div>
              {fromVoice ? (
                <p className="mt-2 text-right text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                  Speak: we are good with this task · or please publish it
                </p>
              ) : null}
            </div>
          </form>
        </div>
  );

  if (isSidebar) return dialog;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleCancel}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        {dialog}
      </div>
    </div>,
    document.body
  );
};

export const VoiceTaskDialogHost = () => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users || []);
  const [open, setOpen] = useState(false);
  const [voiceTitle, setVoiceTitle] = useState('');

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    const onVoice = async (event) => {
      const action = event.detail?.action;
      if (!action) return;
      if (action === 'toast') {
        const message = String(event.detail?.value || '').trim();
        if (message) {
          dispatch(addNotification({ type: 'success', message }));
        }
        return;
      }
      if (['openWorkflow', 'channel', 'callUser', 'reminder', 'createNote', 'openCart', 'addCartItem'].includes(action)) {
        return;
      }
      if (action === 'needCall') {
        const spoken = String(event.detail?.value || '').trim();
        if (!spoken) return;
        try {
          const result = await addNeedToCallFromSpoken(spoken, users);
          if (result?.name && !result.skipped) {
            dispatch(addNotification({
              type: 'success',
              message: `Added ${result.name} to Need to call`,
            }));
          }
        } catch (error) {
          dispatch(addNotification({
            type: 'error',
            message: error?.response?.data?.error || error?.message || 'Could not add to Need to call',
          }));
        }
        return;
      }
      if (window.__workloopVoiceWorkflowOpen) return;
      window.__workloopVoiceTaskOpen = true;
      dispatch(fetchUsers());
      if (action === 'open') {
        setVoiceTitle(String(event.detail?.value || '').trim());
      } else if (!open) {
        setVoiceTitle('');
      }
      setOpen(true);
    };
    window.addEventListener(VOICE_TASK_EVENT, onVoice);
    return () => window.removeEventListener(VOICE_TASK_EVENT, onVoice);
  }, [dispatch, users]);

  useEffect(() => {
    window.__workloopVoiceTaskOpen = open;
    return () => {
      if (!open) window.__workloopVoiceTaskOpen = false;
    };
  }, [open]);

  return (
    <TaskCreationDialog
      isOpen={open}
      fromVoice
      initialTitle={voiceTitle}
      defaultStatus={TaskStatus.PICK_TODAY}
      onClose={() => {
        setOpen(false);
        setVoiceTitle('');
      }}
      onSaved={() => {
        setOpen(false);
        setVoiceTitle('');
      }}
    />
  );
};

export default TaskCreationDialog;
